<template>
    <div>
        <configs-content :loading="$wait.is('fetching.templateCategories')">
            <h2 class="flex items-center text-3xl mb-4">
                <nio-icon class="mr-2" name="regular/email-action-read-document" />
                {{ $t('configs.rightPanel.templates.title') }}
            </h2>

            <section class="mb-8">
                <div class="flex justify-between items-center w-full mb-4">
                    <div class="flex items-center text-xl">
                        <nio-icon
                            class="mr-2"
                            name="regular/folder-bookmark"
                        />
                        <h2>
                            {{ $t('configs.rightPanel.templates.categories') }}
                        </h2>
                    </div>

                    <div class="flex items-center">
                        <div class="relative mr-4">
                            <nio-input
                                :placeholder="$t('global.general.search')"
                                v-model="searchCategory"
                            >
                                <template #after>
                                    <nio-icon name="regular/search" />
                                </template>
                            </nio-input>
                        </div>

                        <nio-button
                            color="blue"
                            icon="regular/add"
                            @click="newTemplateCategory"
                        >
                            {{ $t('global.general.add') }}
                        </nio-button>
                    </div>
                </div>

                <div class="flex flex-col w-full border border-gray-200 rounded">
                    <div class="flex items-center p-4 border-b border-gray-200 rounded-t bg-gray-100">
                        <div class="flex-2">
                            {{ $t('chat.templateCategory.headerName') }}
                        </div>
                        <div class="flex-1 flex justify-end">
                            &nbsp;
                        </div>
                    </div>

                    <div class="flex flex-col items-center w-full h-full">
                        <div class="flex justify-center items-center p-4 w-full h-full" v-if="!templateCategories.length">
                            {{ $t('chat.templateCategory.noCategory') }}
                        </div>

                        <div class="flex justify-center items-center p-4 w-full h-full" v-if="templateCategories.length && !filteredTemplateCategories.length">
                            {{ $t('chat.messageTemplate.noSearchMatch') }}
                        </div>

                        <div
                            class="flex items-center p-4 w-full"
                            :key="category.id"
                            v-for="category in filteredTemplateCategories"
                        >
                            <div class="flex-2">
                                {{ category.name }}
                            </div>

                            <div class="flex-1 flex justify-end items-center space-x-2">
                                <button
                                    class="link-gray text-base"
                                    @click="editTemplateCategory(category)"
                                    v-tooltip="$t('global.general.edit')"
                                >
                                    <nio-icon name="regular/pencil-write" />
                                </button>
                                <button
                                    class="link-gray text-base"
                                    @click="promptDeleteTemplateCategory(category)"
                                    v-tooltip="$t('global.general.delete')"
                                >
                                    <nio-icon name="regular/bin-2-alternate" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <div class="flex justify-between items-center w-full mb-4">
                    <div class="flex items-center text-lg">
                        <nio-icon
                            class="mr-2"
                            name="regular/common-file-text"
                        />

                        <h2>
                            {{ $t('configs.rightPanel.templates.templates') }}
                        </h2>
                    </div>

                    <div class="flex items-center">
                        <div class="relative mr-4">
                            <nio-input
                                :placeholder="$t('global.general.search')"
                                v-model="searchTemplate"
                            >
                                <template #after>
                                    <nio-icon name="regular/search" />
                                </template>
                            </nio-input>
                        </div>

                        <nio-button
                            color="blue"
                            :disabled="empty(templateCategories)"
                            icon="regular/add"
                            @click="newTemplate"
                        >
                            {{ $t('global.general.add') }}
                        </nio-button>
                    </div>
                </div>

                <div class="flex flex-col w-full border border-gray-200 rounded">
                    <div class="flex items-center p-4 border-b border-gray-200 rounded-t bg-gray-100">
                        <div class="flex-2">
                            {{ $t('chat.messageTemplate.headerName') }}
                        </div>
                        <div class="flex-1">
                            {{ $t('chat.messageTemplate.headerCategory') }}
                        </div>
                        <div class="flex-4">
                            {{ $t('chat.messageTemplate.headerContent') }}
                        </div>
                        <div class="flex-1 justify-end">
                            &nbsp;
                        </div>
                    </div>

                    <div class="flex flex-col items-center w-full h-full">
                        <div class="flex justify-center items-center p-4 w-full h-full" v-if="!templates.length">
                            {{ $t('chat.messageTemplate.noTemplate') }}
                        </div>

                        <div class="flex justify-center items-center p-4 w-full h-full" v-if="templates.length && !filteredTemplates.length">
                            {{ $t('chat.messageTemplate.noSearchMatch') }}
                        </div>

                        <div
                            class="flex items-start p-4 w-full | hover:bg-gray-100"
                            :key="template.id"
                            v-for="template in filteredTemplates"
                        >
                            <div class="flex-2 break-anywhere">
                                {{ template.name }}
                            </div>

                            <div class="flex-1 break-anywhere">
                                {{ getCategoryById(template.template_category_id).name }}
                            </div>

                            <div class="flex-4">
                                <span class="break-anywhere" v-html="nl2br(template.content[authUser.locale])" />
                            </div>

                            <div class="flex-1 flex justify-end items-center space-x-2">
                                <button
                                    class="link-gray text-base"
                                    @click="editTemplate(template)"
                                    v-tooltip="$t('global.general.edit')"
                                >
                                    <nio-icon name="regular/pencil-write" />
                                </button>
                                <button
                                    class="link-gray text-base"
                                    @click="promptDeleteTemplate(template)"
                                    v-tooltip="$t('global.general.delete')"
                                >
                                    <nio-icon name="regular/bin-2-alternate" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </configs-content>

        <configs-footer />

        <confirm-modal
            :opened.sync="modals.deleteTemplateOpened"
            :title="$t('chat.messageTemplate.deleteModal.header')"
            @confirm="triggerDeleteTemplate"
        >
            <p slot="message" v-html="$t('chat.messageTemplate.deleteModal.message')" />
        </confirm-modal>
        <confirm-modal
            :opened.sync="modals.deleteCategoryOpened"
            :title="$t('chat.templateCategory.deleteModal.header')"
            @confirm="triggerDeleteTemplateCategory"
        >
            <p slot="message" v-html="$t('chat.templateCategory.deleteModal.message')" />
        </confirm-modal>
        <template-modal />
        <template-category-modal />
    </div>
</template>

<script>
    import orderBy from 'lodash/orderBy.js';
    import { mapState, mapActions, mapMutations } from 'vuex';
    import ConfigsContent from '@/components/ConfigsContent.vue';
    import ConfigsFooter from '@/components/ConfigsFooter.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import TemplateCategoryModal from '@/components/Modals/TemplateCategoryModal.vue';
    import TemplateModal from '@/components/Modals/TemplateModal.vue';

    export default {
        components: {
            ConfigsContent,
            ConfigsFooter,
            ConfirmModal,
            TemplateCategoryModal,
            TemplateModal,
        },

        data() {
            return {
                searchTemplate: '',
                searchCategory: '',
                selectedTemplate: {},
                selectedTemplateCategory: {},
                modals: {
                    deleteTemplateOpened: false,
                    deleteCategoryOpened: false,
                },
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('chat', [
                'templateCategories',
            ]),

            templates() {
                let templates = [];

                for (const category of this.templateCategories) {
                    templates = templates.concat(category.templates);
                }

                return templates;
            },

            filteredTemplateCategories() {
                const search = this.searchCategory.trim();

                return orderBy(
                    this.templateCategories.filter(t => {
                        return search == '' || this.find(t.name, search);
                    }),
                    'name',
                );
            },

            filteredTemplates() {
                const search = this.searchTemplate.trim();

                return orderBy(
                    this.templates.filter(t => {
                        return search == '' ||
                            this.find(t.name, search) ||
                            this.find(t.content[this.authUser.locale], search) ||
                            this.find(this.getCategoryById(t.template_category_id).name, search);
                    }),
                    'name',
                );
            },
        },

        methods: {
            ...mapActions('chat', [
                'fetchTemplateCategories',
                'deleteTemplateCategory',
                'deleteTemplate',
            ]),

            ...mapMutations('chat', [
                'setSelectedTemplateCategory',
                'setSelectedTemplate',
            ]),

            newTemplateCategory() {
                const newCategory = {
                    id: -1,
                    name: '',
                };

                this.setSelectedTemplateCategory(newCategory);
                this.$modal.open('templateCategory');
            },

            editTemplateCategory(category) {
                this.setSelectedTemplateCategory(category);
                this.$modal.open('templateCategory');
            },

            promptDeleteTemplateCategory(category) {
                this.selectedTemplateCategory = category;
                this.modals.deleteCategoryOpened = true;
            },

            async triggerDeleteTemplateCategory() {
                try {
                    await this.deleteTemplateCategory(this.selectedTemplateCategory);

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('chat.templateCategoryModal.successes.deleted'),
                    });

                    this.fetchTemplateCategories();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('chat.templateCategoryModal.errors.deleting'),
                    });

                    throw error;
                }
            },

            promptDeleteTemplate(template) {
                this.selectedTemplate = template;
                this.modals.deleteTemplateOpened = true;
            },

            async triggerDeleteTemplate() {
                try {
                    await this.deleteTemplate(this.selectedTemplate);

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('chat.messageTemplateModal.successes.deleted'),
                    });

                    this.fetchTemplateCategories();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('chat.messageTemplateModal.errors.deleting'),
                    });

                    throw error;
                }
            },

            newTemplate() {
                if (empty(this.templateCategories)) {
                    return;
                }

                const newTemplate = {
                    subject: {
                        fr: '',
                        en: '',
                    },
                    content: {
                        fr: '',
                        en: '',
                    },
                    id: -1,
                    name: '',
                    template_category_id: null,
                };

                this.setSelectedTemplate(newTemplate);
                this.$modal.open('template');
            },

            editTemplate(template) {
                this.setSelectedTemplate(template);
                this.$modal.open('template');
            },

            getCategoryById(id) {
                return this.templateCategories.find(t => t.id == id);
            },
        },
    };
</script>
